import { Injectable, inject } from '@angular/core';
import {
	BehaviorSubject,
	Observable,
	catchError,
	tap,
	throwError,
	share,
	finalize,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { LoginResponse } from '@meta-press/shared-plugins';

@Injectable({
	providedIn: 'root',
})
export class AuthTokenService {
	private accessToken = '';
	private refreshToken = '';
	private tokenExpiration: number = 0; // Unix timestamp in milliseconds

	// Token refresh timer state
	private refreshTimer: any = null;
	private idleTimer: any = null;
	private tokenRefreshStoppedDueInactivity = false;
	private http = inject(HttpClient);

	// Login process state tracking
	private loginInProgressState = false;

	// Refresh synchronization - prevent multiple concurrent refresh calls
	private refreshInProgress: Observable<LoginResponse | undefined> | null =
		null;

	// Observable for token changes
	private tokenSubject = new BehaviorSubject<{
		accessToken: string;
		refreshToken: string;
		tokenExpiration: number;
	}>({
		accessToken: '',
		refreshToken: '',
		tokenExpiration: 0,
	});

	public tokens$ = this.tokenSubject.asObservable();

	/**
	 * Sets the authentication tokens
	 * @param accessToken The access token
	 * @param refreshToken The refresh token
	 * @param expiresIn Token lifetime in seconds
	 */
	public setTokens(
		accessToken: string,
		refreshToken: string,
		expiresIn: number,
	): void {
		this.accessToken = accessToken;
		this.refreshToken = refreshToken;
		// Convert expiresIn from seconds to milliseconds
		this.tokenExpiration = Date.now() + expiresIn * 1000;

		// console.log('Tokens set - expires in:', expiresIn, 'seconds, at:', new Date(this.tokenExpiration).toISOString());

		// Notify subscribers
		this.tokenSubject.next({
			accessToken: this.accessToken,
			refreshToken: this.refreshToken,
			tokenExpiration: this.tokenExpiration,
		});
	}

	/**
	 * Clears authentication tokens
	 */
	public clearTokens(): void {
		this.accessToken = '';
		this.refreshToken = '';
		this.tokenExpiration = 0;

		// Reset login progress state when clearing tokens
		this.loginInProgressState = false;

		// Notify subscribers
		this.tokenSubject.next({
			accessToken: '',
			refreshToken: '',
			tokenExpiration: 0,
		});
	}

	/**
	 * Gets the current access token
	 */
	public getAccessToken(): string {
		return this.accessToken;
	}

	/**
	 * Gets the current refresh token
	 */
	public getRefreshToken(): string {
		return this.refreshToken;
	}

	/**
	 * Gets the token expiration timestamp
	 */
	public getTokenExpiration(): number {
		return this.tokenExpiration;
	}

	/**
	 * Sets the login process state
	 * @param inProgress true if login is in progress, false otherwise
	 */
	public setLoginInProgress(inProgress: boolean): void {
		this.loginInProgressState = inProgress;
		console.log('Login process state set to:', inProgress);
	}

	/**
	 * Checks if login is currently in progress
	 * @returns true if login is in progress
	 */
	public isLoginInProgress(): boolean {
		return this.loginInProgressState;
	}

	/**
	 * Checks if tokens are available
	 */
	public hasTokens(): boolean {
		return !!(this.accessToken && this.refreshToken);
	}

	/**
	 * Checks if the access token is expired
	 */
	public isTokenExpired(): boolean {
		return Date.now() >= this.tokenExpiration;
	}

	/**
	 * Checks if token will expire soon (within given milliseconds)
	 * @param milliseconds Time window in milliseconds to check if token is expiring soon
	 */
	public isTokenExpiringSoon(milliseconds: number): boolean {
		const timeUntilExpiry = this.tokenExpiration - Date.now();
		const isExpiringSoon = timeUntilExpiry <= milliseconds;

		// Only log if token is expiring soon to reduce noise
		if (isExpiringSoon) {
			console.log(
				'Token expiring soon - minutes until expiry:',
				Math.round(timeUntilExpiry / (1000 * 60)),
			);
		}

		return isExpiringSoon;
	}

	/**
	 * Notifies the service of user activity to keep the auth session alive
	 * This resets both the token refresh timer and idle timeout timer
	 */
	public notifyUserActivity(): void {
		// console.debug('User activity detected - resetting idle timer');

		// If refresh timer was stopped due to inactivity or not running, restart it
		if (!this.isTimerRunning() || this.tokenRefreshStoppedDueInactivity) {
			this.startTokenRefreshTimer(
				environment.authTokenRefreshIntervalMinutes,
				true,
			);
		} else {
			// Timer is running, just reset the idle timer
			if (environment.authTokenRefreshGapInMinutes > -1) {
				this.startIdleTimer(environment.authTokenRefreshGapInMinutes);
			}
		}
	}

	/**
	 * Starts the token refresh timer that will refresh the token at the specified interval
	 * @param intervalMinutes How often to refresh the token (in minutes)
	 * @param startIdleTimeout Whether to start the idle timeout timer
	 */
	public startTokenRefreshTimer(
		intervalMinutes: number,
		startIdleTimeout = true,
	): void {
		// Stop any existing timers first
		this.stopTokenRefreshTimer();
		this.tokenRefreshStoppedDueInactivity = false;

		const intervalMs = intervalMinutes * 60 * 1000;
		console
			.debug
			// `Starting token refresh timer - interval: ${intervalMinutes} minutes`,
			();

		this.refreshTimer = setInterval(() => {
			console.debug('Auto-refreshing token...');
			this.refreshAccessToken().subscribe({
				next: () => {
					console.debug('Token refreshed successfully');
				},
				error: (error) => {
					console.error('Failed to refresh token:', error);
					// Stop the timer if refresh fails
					this.stopTokenRefreshTimer();
				},
			});
		}, intervalMs);

		// Start idle timer if requested and configured
		if (environment.authTokenRefreshGapInMinutes > -1 && startIdleTimeout) {
			this.startIdleTimer(environment.authTokenRefreshGapInMinutes);
		}
	}

	/**
	 * Stops the token refresh timer and idle timer
	 */
	public stopTokenRefreshTimer(): void {
		if (this.refreshTimer) {
			// console.debug('Stopping token refresh timer');
			clearInterval(this.refreshTimer);
			this.refreshTimer = null;
		}
		// Also stop the idle timer
		this.stopIdleTimer();
	}

	/**
	 * Checks if the timer is currently running
	 */
	public isTimerRunning(): boolean {
		return this.refreshTimer !== null;
	}

	/**
	 * Starts the idle timer that will stop refresh after maximum idle duration
	 * @param idleMinutes How long to wait before considering user idle (in minutes)
	 */
	private startIdleTimer(idleMinutes: number): void {
		// Stop any existing idle timer first
		this.stopIdleTimer();

		if (idleMinutes > 0) {
			const idleMs = idleMinutes * 60 * 1000;
			// console.debug(
			// 	`Starting idle timer for ${idleMinutes} minutes (${idleMs}ms)`,
			// );

			this.idleTimer = setTimeout(() => {
				console.debug(
					'Token refresh timer stopped after maximum idle duration',
				);
				this.stopTokenRefreshTimer();
				this.tokenRefreshStoppedDueInactivity = true;
			}, idleMs);
		}
	}

	/**
	 * Stops the idle timer
	 */
	private stopIdleTimer(): void {
		if (this.idleTimer) {
			// console.debug('Stopping idle timer');
			clearTimeout(this.idleTimer);
			this.idleTimer = null;
		}
	}

	/**
	 * Refreshes the access token using the refresh token
	 * Uses synchronization to prevent multiple concurrent refresh calls
	 */
	public refreshAccessToken(): Observable<LoginResponse | undefined> {
		// If a refresh is already in progress, return the existing observable
		if (this.refreshInProgress) {
			console.debug(
				'Token refresh already in progress, reusing existing call',
			);
			return this.refreshInProgress;
		}

		const refreshRequest = {
			refreshToken: this.getRefreshToken(),
		};

		console.debug('Starting new token refresh');

		// Create a shared observable to prevent multiple concurrent refresh calls
		this.refreshInProgress = this.http
			.post<
				LoginResponse | undefined
			>(`${environment.apiUrl}/refresh`, refreshRequest)
			.pipe(
				tap((response: LoginResponse | undefined) => {
					// Handle successful token refresh
					if (
						response?.accessToken &&
						response?.refreshToken &&
						response?.expiresIn
					) {
						this.setTokens(
							response.accessToken,
							response.refreshToken,
							response.expiresIn,
						);
						console.debug(
							'Token refreshed, new access token:',
							response.accessToken,
						);
					} else {
						console.error('Invalid Refresh response:', response);
						throw new Error('Invalid Refresh response');
					}
				}),
				catchError((error: unknown) => {
					// Handle error here
					console.error('Refresh error:', error);
					return throwError(() => new Error('Refresh failed'));
				}),
				// Use finalize to clean up the shared observable when done
				finalize(() => {
					console.debug(
						'Token refresh completed, clearing shared observable',
					);
					this.refreshInProgress = null;
				}),
				// Share the observable so multiple subscribers get the same result
				share(),
			);

		return this.refreshInProgress;
	}
}
